import _ from 'underscore';
import React, { useState, useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Toggle } from '@fluentui/react';
import { Dropdown, TextField } from '@bingads-webui-react/fabric-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withCustomValidation, withValidation, withError } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';

const ValidatedTextField = withValidation(TextField);

const FrequencyIntervals = {
  Hour: 'HOUR',
  Day: 'DAY',
  Week: 'WEEK',
  Month: 'MONTH',
  Lifetime: 'LIFETIME',
};

const FrequencyCapUnit = 'IMPRESSION';

const getKeyStringMap = i18n => [
  { key: FrequencyIntervals.Hour, text: i18n.getString(_TL_('hour')) },
  { key: FrequencyIntervals.Day, text: i18n.getString(_TL_('day')) },
  { key: FrequencyIntervals.Week, text: i18n.getString(_TL_('week')) },
];

export const formatFrequencyCapping = ({ CapValue, TimeGranularity, i18n }) => {
  let interval;
  if (TimeGranularity === undefined || TimeGranularity === null) {
    return i18n.getString(_TL_('No limit to how often people see your ads'));
  }
  if (TimeGranularity === '') {
    interval = '';
  } else {
    interval = getKeyStringMap(i18n).find(({ key }) => key === TimeGranularity).text;
  }
  return i18n.getString(_TL_('{{count}} times per {{interval}}'), { count: CapValue, interval });
};

const textWidth = 100;
const textStyles = { root: { width: textWidth } };

const dropdownWidth = 100;
const dropDownStyles = { dropdown: { width: dropdownWidth } };

const getStyle = () => ({
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  frequency: {
    margin: '0 10px',
  },
});

const ValueAndInterval = ({
  i18n,
  TimeGranularity,
  onIntervalChanged,
  timeOptions,
  validationProps,
}) => {
  const classes = useDefaultStyles(getStyle);

  return (
    <div className={classNames(classes.inputContainer, 'frequency-capping-inputs')} >
      <ValidatedTextField
        styles={textStyles}
        id="input-frequency-capping-value"
        validationProps={validationProps}
        ariaLabel={i18n.getString(_TL_('cap value'))}
      />
      <span className={classes.frequency}>{formatFrequencyCapping({ CapValue: '', TimeGranularity: '', i18n })}</span>
      <Dropdown
        options={timeOptions}
        id="input-frequency-capping-interval"
        onChange={onIntervalChanged}
        styles={dropDownStyles}
        selectedKey={[TimeGranularity]}
        ariaLabel={i18n.getString(_TL_('time granularity'))}
        className="frequency-capping-dropdown"
      />
    </div>
  );
};

ValueAndInterval.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  TimeGranularity: PropTypes.string,
  onIntervalChanged: PropTypes.func.isRequired,
  timeOptions: PropTypes.shape({}).isRequired,
  validationProps: PropTypes.shape({}).isRequired,
};

ValueAndInterval.defaultProps = {
  TimeGranularity: '',
};

const ValidatedValueAndInterval = withError(ValueAndInterval);

const FrequencyCapping = (props) => {
  const {
    i18n: newI18n,
    legacyI18n,
    validationProps,
    updateValidation,
  } = props;

  const i18n = useMemo(() => mergeI18n(legacyI18n, newI18n), [legacyI18n, newI18n]);
  const activity = useContext(ActivityContext);
  const { FrequencyCapSettings } = validationProps;
  const { CapValue = null, TimeGranularity } = FrequencyCapSettings[0] || {};
  const [errorMessage, setErrorMessage] = useState([]);

  const onIntervalChanged = useCallback((e, value) => {
    updateValidation(errorMessage, {
      FrequencyCapSettings: [{
        CapValue,
        TimeGranularity: value.key,
        FrequencyCapUnit,
      }],
    });

    if (activity) {
      activity.signal(`onFrequencyIntervalChanged - ${JSON.stringify(value)})}`);
    }
  }, [updateValidation, errorMessage, CapValue, activity]);

  const timeOptions = useMemo(() => getKeyStringMap(i18n), [i18n]);

  const onToggle = useCallback((e, checked) => {
    if (checked) {
      updateValidation([], {
        FrequencyCapSettings: [{
          CapValue: undefined,
          TimeGranularity: TimeGranularity || FrequencyIntervals.Hour,
          FrequencyCapUnit,
        }],
      });
    } else {
      updateValidation([], { FrequencyCapSettings: [{}] });
    }

    if (activity) {
      activity.signal(`onFrequencyCapToggleChanged - toggleStatus: ${checked}`);
    }
  }, [TimeGranularity, activity, updateValidation]);

  const onCountValidate = useCallback((result) => {
    const { messages = [], data: { capValue } } = result;
    if (!_.isEqual(messages, errorMessage) || capValue !== CapValue) {
      setErrorMessage(messages);

      updateValidation(messages, {
        FrequencyCapSettings: [{
          CapValue: capValue,
          TimeGranularity,
          FrequencyCapUnit,
        }],
      });
    }
  }, [errorMessage, CapValue, updateValidation, TimeGranularity]);

  const customErrors = {
    decimalLength: i18n.getString(_TL_('Field requires integer value')),
  };

  const errorProcessor = errors => _.map(
    errors,
    (oldError, key) => customErrors[key] || oldError
  );

  return (
    <React.Fragment>
      <Toggle
        label={i18n.getString(_TL_('Frequency limit'))}
        onText={i18n.getString(_TL_('On: Limit how often a potential customer sees your ads.'))}
        offText={i18n.getString(_TL_('Off'))}
        onChange={onToggle}
        checked={!!TimeGranularity}
        className="frequency-capping-toggle"
      />
      { TimeGranularity && (
        <ValidatedValueAndInterval
          i18n={i18n}
          TimeGranularity={TimeGranularity}
          onIntervalChanged={onIntervalChanged}
          timeOptions={timeOptions}
          validationProps={{
            i18n,
            data: { capValue: CapValue },
            onValidate: onCountValidate,
            field: 'capValue',
            alwaysShowError: true,
            errorProcessor,
            schema: {
              properties: {
                capValue: {
                  type: 'integer',
                  minimum: 1,
                  maximum: 255,
                  required: true,
                },
              },
            },
          }}
        />
      )}
    </React.Fragment>
  );
};

FrequencyCapping.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    inputContainer: PropTypes.string.isRequired,
    frequency: PropTypes.string.isRequired,
  }).isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      CapValue: PropTypes.string,
      TimeGranularity: PropTypes.string,
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
};

const ValidatedFrequencyCapping = withCustomValidation(FrequencyCapping);

export {
  FrequencyCapping,
  ValidatedFrequencyCapping,
};
